import { create } from 'zustand';
import { message } from 'antd';
import { fetchFeed, hasFeedSearchFilter } from '../../../services/appOrgProjectApi';

const useProductStore = create((set, get) => ({
  api: null,
  project: null,
  feedPage: null,
  feedPageIndex: 0,
  productIndex: 0,
  product: null,
  productFields: {},
  reloadingProducts: false,
  isSaving: false,
  feedSearchEmptyResult: false,
  feedListQuery: {},

  init: (api) => set({ api }),

  setProject: (project) => set({ project }),
  setFeedPage: (feedPage) => set({ feedPage }),
  setFeedPageIndex: (feedPageIndex) => set({ feedPageIndex }),
  setProductIndex: (productIndex) => set({ productIndex }),
  setProduct: (product) => set({ product }),
  setProductFields: (productFields) => set({ productFields }),
  setReloadingProducts: (reloadingProducts) => set({ reloadingProducts }),
  setIsSaving: (isSaving) => set({ isSaving }),

  loadProject: async (projectId) => {
    const { api } = get();
    try {
      const data = await api.fetchProject(projectId);
      set({ project: data });
      if (data?.feed?.feed_fields) {
        set({ productFields: JSON.parse(data.feed.feed_fields) });
      }
      return data;
    } catch (error) {
      message.error(error?.message);
    }
  },

  clearFeedListQuery: () => set({ feedListQuery: {} }),

  resetFeedSearch: async () => {
    const { feedListQuery } = get();
    if (!hasFeedSearchFilter(feedListQuery)) return;
    get().clearFeedListQuery();
    await get().loadFeed();
  },

  loadFeed: async () => {
    const { project, feedPageIndex, feedListQuery = {} } = get();
    set({
      reloadingProducts: true,
      feedPage: null,
      product: null,
      feedSearchEmptyResult: false,
    });
    const feedId = project?.feed?.id;
    if (!feedId) {
      set({ reloadingProducts: false });
      return;
    }
    try {
      const data = await fetchFeed({
        feedId,
        pageNum: feedPageIndex,
        productId: feedListQuery.productId,
        searchField: feedListQuery.searchField,
        searchValue: feedListQuery.searchValue,
      });
      set({ feedPage: data });

      if (hasFeedSearchFilter(feedListQuery) && !data?.products?.length) {
        set({ feedSearchEmptyResult: true, product: null });
      } else if (data?.products?.length) {
        const idx = get().productIndex % data.page_size;
        if (idx < data.products.length) {
          set({ product: data.products[idx] });
        }
      }
    } catch (error) {
      message.error(error?.message);
    } finally {
      set({ reloadingProducts: false });
    }
  },

  changeProduct: () => {
    const { feedPage, productIndex } = get();
    if (!feedPage) return;
    const currentIndex = productIndex % feedPage.page_size;
    if (feedPage.products?.length && currentIndex < feedPage.products.length) {
      set({ product: feedPage.products[currentIndex] });
    }
  },

  toNextProduct: (step = 1) => {
    const { feedPage, productIndex, feedPageIndex, isSaving } = get();
    if (!feedPage || isSaving) return;
    const nextProduct = productIndex + step;
    const nextPage = parseInt(nextProduct / feedPage.page_size);
    if (nextProduct >= feedPage.total_products) {
      set({ feedPageIndex: 0, productIndex: 0, reloadingProducts: true });
    } else if (nextPage > feedPageIndex) {
      set({ feedPageIndex: nextPage, productIndex: nextProduct, reloadingProducts: true });
    } else {
      set({ productIndex: nextProduct });
    }
  },

  toPrevProduct: (step = 1) => {
    const { feedPage, productIndex, feedPageIndex, isSaving } = get();
    if (!feedPage || isSaving) return;
    const prevProduct = productIndex - step;
    const prevPage = parseInt(prevProduct / feedPage.page_size);
    if (prevProduct < 0) {
      set({
        feedPageIndex: feedPage.max_pages,
        productIndex: feedPage.total_products - 1,
        reloadingProducts: true,
      });
    } else if (prevPage < feedPageIndex) {
      set({ feedPageIndex: prevPage, productIndex: prevProduct, reloadingProducts: true });
    } else {
      set({ productIndex: prevProduct });
    }
  },

  toSelectProduct: (index) => set({ productIndex: index }),

  toFeedPageSelect: (page) => set({ feedPageIndex: page }),

  searchProductById: async (productId, searchField, searchValue) => {
    const { project } = get();
    if (!project?.feed?.id) return;
    set({
      feedListQuery: { productId, searchField, searchValue },
      feedPageIndex: 0,
      productIndex: 0,
    });
    await get().loadFeed();
  },

  reset: () =>
    set({
      project: null,
      feedPage: null,
      feedPageIndex: 0,
      productIndex: 0,
      product: null,
      productFields: {},
      reloadingProducts: false,
      isSaving: false,
      feedSearchEmptyResult: false,
      feedListQuery: {},
    }),
}));

export default useProductStore;
